/**
 * Uploads API — file upload helpers shared across the app.
 *
 * Server endpoints (all under /api/cargo/upload):
 *   POST /image     — single image (jpg/png/webp/gif), returns signed URL
 *   POST /images    — up to 5 images
 *   POST /document  — single doc (pdf, scanned image), 10MB cap, used by KYC
 *   POST /instructions — wider doc whitelist (xlsx, doc, etc) for container guides
 *
 * Each endpoint returns:
 *   { success: true, data: { url: string, filename: string } }
 *
 * The server uses a separate Dio-equivalent multipart upload — we wire
 * native FormData here.
 */
import { api } from './client';
import { unwrap } from './_unwrap';

interface UploadResult {
  url: string;
  filename: string;
}

async function postFile(path: string, formField: string, file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append(formField, file);
  // Don't set Content-Type — the browser auto-fills the boundary.
  const r = await api.post(path, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  });
  return unwrap<UploadResult>(r);
}

export const uploadsApi = {
  uploadImage: (file: File) => postFile('/api/cargo/upload/image', 'image', file),
  uploadDocument: (file: File) => postFile('/api/cargo/upload/document', 'file', file),
  uploadInstructions: (file: File) => postFile('/api/cargo/upload/instructions', 'file', file),
};
